// frontend/src/pages/PortadorDetailPage/PortadorDetailPage.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Typography, Table, Button, message, Spin, Tag, Card, Row, Col, List, Modal, Space, Tooltip } from 'antd';
import {
    ArrowLeftOutlined, PlusOutlined, UserOutlined, IdcardOutlined,
    CheckCircleOutlined, CloseCircleOutlined, StopOutlined,
} from '@ant-design/icons';
import { getPortadorDetail, desativarProgramaPortador } from '../../services/milhasService';
import { getErrorMessage } from '../../utils/errorHandler';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import AtivarProgramaModal from '../../components/AtivarProgramaModal/AtivarProgramaModal';
import './PortadorDetailPage.css';

const { Title, Text } = Typography;
const { useModal } = Modal;

const PortadorDetailPage = () => {
    const { id } = useParams();
    const [portador, setPortador] = useState(null);
    const [programas, setProgramas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAtivarProgramaModalVisible, setIsAtivarProgramaModalVisible] = useState(false);
    const [desativandoId, setDesativandoId] = useState(null);

    const [modal, modalContextHolder] = useModal();
    const [messageApi, messageContextHolder] = message.useMessage();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const portadorRes = await getPortadorDetail(id);
            setPortador(portadorRes.data);
            setProgramas(portadorRes.data.programas || []);
        } catch (error) {
            messageApi.error(getErrorMessage(error, 'Erro ao buscar dados do portador.'));
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [id, messageApi]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const programasAtivos = useMemo(
        () => programas.filter((p) => !p.status || p.status === 'ATIVO'),
        [programas],
    );

    const handleOpenAtivarProgramaModal = () => setIsAtivarProgramaModalVisible(true);
    const handleCloseAtivarProgramaModal = () => setIsAtivarProgramaModalVisible(false);
    const handleAtivarProgramaSuccess = () => {
        handleCloseAtivarProgramaModal();
        fetchData();
    };

    const saldoZerado = (item) => Number(item?.saldo_atual || 0) === 0;

    const handleDesativarPrograma = (item) => {
        if (!saldoZerado(item)) {
            messageApi.warning('Só é possível desativar o programa quando o saldo estiver zerado.');
            return;
        }
        modal.confirm({
            title: 'Desativar programa?',
            content: (
                <span>
                    O programa <strong>{item.programa_nome}</strong> será desativado para este portador.
                    O histórico de movimentações é preservado. Você poderá ativá-lo de novo depois.
                </span>
            ),
            okText: 'Sim, desativar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                setDesativandoId(item.id);
                try {
                    await desativarProgramaPortador(item.id);
                    messageApi.success('Programa desativado com sucesso.');
                    await fetchData();
                } catch (error) {
                    messageApi.error(getErrorMessage(error, 'Erro ao desativar o programa.'));
                } finally {
                    setDesativandoId(null);
                }
            },
        });
    };

    const renderDesativarBotao = (item, block = false) => {
        const podeDesativar = saldoZerado(item);
        const botao = (
            <Button
                danger
                type={block ? 'default' : 'link'}
                size="small"
                icon={<StopOutlined />}
                disabled={!podeDesativar}
                loading={desativandoId === item.id}
                onClick={() => handleDesativarPrograma(item)}
                block={block}
            >
                Desativar
            </Button>
        );
        if (podeDesativar) return botao;
        return (
            <Tooltip title="Zere o saldo deste programa para poder desativá-lo.">
                <span style={{ display: block ? 'block' : 'inline-block' }}>{botao}</span>
            </Tooltip>
        );
    };

    const programasColumns = [
        {
            title: 'Programa de Fidelidade',
            dataIndex: 'programa_nome',
            key: 'programa_nome',
        },
        {
            title: 'Saldo Atual',
            dataIndex: 'saldo_atual',
            key: 'saldo_atual',
            align: 'right',
            render: (saldo) => parseFloat(saldo).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
        },
        {
            title: 'Custo Médio do Milheiro',
            dataIndex: 'custo_medio_milheiro',
            key: 'custo_medio_milheiro',
            align: 'right',
            render: (cpm) => parseFloat(cpm).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        },
        {
            title: 'Ações',
            key: 'acoes',
            width: 140,
            align: 'center',
            render: (_, record) => renderDesativarBotao(record),
        },
    ];

    if (loading) {
        return <div className="loading-container"><Spin size="large" /></div>;
    }

    if (!portador) {
        return <Title level={3}>Portador não encontrado.</Title>;
    }

    return (
        <div style={{ padding: isMobile ? '16px' : '0' }}>
            {messageContextHolder}
            {modalContextHolder}
            <div style={{ marginBottom: '24px' }}>
                <Link to="/milhas/portadores">
                    <Button icon={<ArrowLeftOutlined />} block={isMobile} size={isMobile ? 'large' : 'middle'}>
                        {isMobile ? 'Voltar' : 'Voltar para Gestão de Portadores'}
                    </Button>
                </Link>
            </div>

            <Card style={{ marginBottom: 24 }} styles={{ body: { padding: isMobile ? '16px' : '24px' } }}>
                <div style={{ marginBottom: 16 }}>
                    <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>{portador.nome_completo}</Title>
                </div>

                {isMobile ? (
                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            <div className="detail-item">
                                <Text type="secondary" style={{ fontSize: 12 }}>CPF</Text>
                                <div style={{ fontSize: 16, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <IdcardOutlined style={{ color: '#50B0A0' }} />
                                    {portador.cpf}
                                </div>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="detail-item">
                                <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                                <div style={{ marginTop: 4 }}>
                                    <Tag color={portador.status === 'ATIVO' ? 'success' : 'error'} style={{ margin: 0, fontSize: 14, padding: '2px 8px' }}>
                                        {portador.status === 'ATIVO' ? <CheckCircleOutlined /> : <CloseCircleOutlined />} {portador.status}
                                    </Tag>
                                </div>
                            </div>
                        </Col>
                        <Col span={24}>
                            <div className="detail-item full-width-item" style={{ background: 'var(--color-surface-muted, #F8F4EC)', padding: '8px 12px', borderRadius: 6 }}>
                                <Row justify="space-between" align="middle">
                                    <Text type="secondary">É Portador Principal?</Text>
                                    <Text strong>
                                        {portador.is_principal ? (
                                            <Tag color="gold" icon={<UserOutlined />}>SIM</Tag>
                                        ) : (
                                            <Tag>NÃO</Tag>
                                        )}
                                    </Text>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <Row gutter={[48, 24]}>
                        <Col span={8}>
                            <Text type="secondary">CPF</Text>
                            <div style={{ fontSize: 18 }}>{portador.cpf}</div>
                        </Col>
                        <Col span={8}>
                            <Text type="secondary">Status</Text>
                            <div><Tag color={portador.status === 'ATIVO' ? 'green' : 'red'}>{portador.status}</Tag></div>
                        </Col>
                        <Col span={8}>
                            <Text type="secondary">Portador Principal</Text>
                            <div>{portador.is_principal ? 'Sim' : 'Não'}</div>
                        </Col>
                    </Row>
                )}
            </Card>

            <Card styles={{ body: { padding: isMobile ? '12px' : '24px' } }}>
                <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                    <Col xs={24} sm={12}>
                        <Title level={4} style={{ margin: 0 }}>Programas Ativos</Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Desative um programa com saldo zerado se não for mais usar neste portador.
                        </Text>
                    </Col>
                    <Col xs={24} sm={12} style={{ textAlign: isMobile ? 'left' : 'right' }}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenAtivarProgramaModal} block={isMobile} size={isMobile ? 'large' : 'middle'}>
                            Ativar Novo Programa
                        </Button>
                    </Col>
                </Row>

                {isMobile ? (
                    <List
                        dataSource={programasAtivos}
                        locale={{
                            emptyText: (
                                <EmptyStateGuide
                                    title="Nenhum programa ativo."
                                    description="Ative um programa de fidelidade para este portador e comece a registrar milhas."
                                    actionLabel="Ativar programa"
                                    onAction={handleOpenAtivarProgramaModal}
                                />
                            ),
                        }}
                        renderItem={(item) => (
                            <Card
                                style={{ marginBottom: 12, borderRadius: 8, border: '1px solid #f0f0f0' }}
                                styles={{ body: { padding: '16px' } }}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '8px' }}>
                                        <Text strong style={{ fontSize: '1.1rem', color: 'var(--ant-color-text)' }}>
                                            {item.programa_nome}
                                        </Text>
                                    </div>

                                    <Row gutter={16} align="bottom">
                                        <Col span={12}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <Text type="secondary" style={{ fontSize: '0.85rem' }}>Saldo Atual</Text>
                                                <span style={{ fontSize: '1.2rem', fontWeight: 600 }}>
                                                    {parseFloat(item.saldo_atual).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        </Col>
                                        <Col span={12} style={{ textAlign: 'right' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <Text type="secondary" style={{ fontSize: '0.85rem' }}>Custo Médio</Text>
                                                <span style={{ fontSize: '1.2rem', fontWeight: 600, color: '#D64545' }}>
                                                    {parseFloat(item.custo_medio_milheiro).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                </span>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {renderDesativarBotao(item, true)}
                                    </Space>
                                </div>
                            </Card>
                        )}
                    />
                ) : (
                    <Table
                        columns={programasColumns}
                        dataSource={programasAtivos}
                        rowKey="id"
                        pagination={false}
                        locale={{
                            emptyText: (
                                <EmptyStateGuide
                                    title="Nenhum programa ativo para este portador."
                                    description="Ative um programa de fidelidade para começar a registrar milhas."
                                    actionLabel="Ativar programa"
                                    onAction={handleOpenAtivarProgramaModal}
                                />
                            ),
                        }}
                    />
                )}
            </Card>

            {isAtivarProgramaModalVisible && (
                <AtivarProgramaModal
                    visible={isAtivarProgramaModalVisible}
                    onClose={handleCloseAtivarProgramaModal}
                    onSuccess={handleAtivarProgramaSuccess}
                    portadorId={id}
                    isMobile={isMobile}
                />
            )}
        </div>
    );
};

export default PortadorDetailPage;
